/**
 * <PathMarker> — Marker geometry placed at a path point with sensible
 * default orientation per shape.
 *
 * Centroid invariant: every shape is rendered with its CENTRE at the
 * path point (the `position` argument). This keeps stop-position
 * markers visually anchored on the path tube regardless of geometry
 * — cones and cylinders straddle the path half above / half below
 * rather than sitting on top of it.
 *
 * Shape semantics:
 *   - Diamond  : OctahedronGeometry, +X aligned with tangent
 *   - Tick     : narrow BoxGeometry across the path
 *   - Sphere   : isotropic, no orientation
 *   - Cone     : axis = world +Z, apex up, centre on path
 *   - Cube     : cube oriented along path tangent (chunkier than Tick)
 *   - Cylinder : pillar with axis = world +Z, centre on path
 *   - None     : nothing rendered
 */

import React, { useMemo } from 'react';
import { Quaternion, Vector3 } from 'three';
import type { MarkerShape } from '../../types.js';

interface PathMarkerProps {
  position: Vector3;
  tangent: Vector3;
  shape: MarkerShape;
  sizeMm: number;
  color: string | number;
}

export const PathMarker: React.FC<PathMarkerProps> = ({
  position,
  tangent,
  shape,
  sizeMm,
  color,
}) => {
  const tangentQuat = useMemo(() => {
    const q = new Quaternion();
    q.setFromUnitVectors(new Vector3(1, 0, 0), tangent.clone().normalize());
    return q;
  }, [tangent]);

  if (shape === 'None') return null;

  const half = sizeMm / 2;

  // Pick geometry + a per-shape lift so the marker visually sits "on" the
  // path point rather than passing through it (cones/cylinders have their
  // mass above the base).
  switch (shape) {
    case 'Diamond':
      return (
        <mesh position={position} quaternion={tangentQuat}>
          <octahedronGeometry args={[half, 0]} />
          <meshStandardMaterial color={color as string} />
        </mesh>
      );
    case 'Tick':
      return (
        <mesh position={position} quaternion={tangentQuat}>
          <boxGeometry args={[sizeMm * 0.4, sizeMm * 1.5, sizeMm * 0.6]} />
          <meshStandardMaterial color={color as string} />
        </mesh>
      );
    case 'Sphere':
      return (
        <mesh position={position}>
          <sphereGeometry args={[half, 16, 12]} />
          <meshStandardMaterial color={color as string} />
        </mesh>
      );
    case 'Cube':
      return (
        <mesh position={position} quaternion={tangentQuat}>
          <boxGeometry args={[sizeMm, sizeMm, sizeMm]} />
          <meshStandardMaterial color={color as string} />
        </mesh>
      );
    case 'Cone': {
      // ConeGeometry's default axis is +Y; rotate so the apex points +Z.
      // Centre on the path — the cone straddles the path half above /
      // half below so the geometric centre lines up with the tube.
      return (
        <mesh position={position} rotation={[Math.PI / 2, 0, 0]}>
          <coneGeometry args={[half, sizeMm, 16]} />
          <meshStandardMaterial color={color as string} />
        </mesh>
      );
    }
    case 'Cylinder': {
      // CylinderGeometry's default axis is +Y; rotate so it stands along
      // world +Z (a pillar). Centred on the path for the same reason as
      // Cone — keeps the marker symmetric around the path tube.
      return (
        <mesh position={position} rotation={[Math.PI / 2, 0, 0]}>
          <cylinderGeometry
            args={[half * 0.6, half * 0.6, sizeMm, 16]}
          />
          <meshStandardMaterial color={color as string} />
        </mesh>
      );
    }
    default:
      return null;
  }
};
